<!--
  Small themed confirmation dialog for the slot-booking feature (removing a
  resource, cancelling a booking) -- reuses the exact same .caller-dialog
  pattern CampaignConfirmDialog.vue already uses for pause/delete campaign,
  instead of the earlier inline-expanding confirm strip. Ramu, 2026-09-04:
  "You can give dialogue warnings, right?... why aren't you giving the
  dialogue, instead showing something [inline] there itself?" -- a short
  yes/no confirmation is exactly the case a small styled dialog suits (it's
  meant to interrupt and demand one clear decision), which is a different
  need from the big multi-field resource-editing form he explicitly asked
  to NOT be a dialog earlier this same session. Generic props instead of
  CampaignConfirmDialog's action-keyed copy map, since there's no shared
  "subject" concept here (a resource vs. a booking) worth forcing into one.
-->
<template>
  <!--
    v-if, not just :model-value -- this pinned Vuetify beta
    (^3.0.0-beta.0) has a real bug where v-dialog's content sometimes fails
    to unmount when model-value flips back to false programmatically (a
    Cancel/close click): the Vue state (pendingDeleteId etc. in the parent)
    updates correctly and immediately, confirmed via direct reactive-state
    inspection, but the rendered .caller-dialog DOM node stays put
    indefinitely -- not a transition delay, still there 2.5s later.
    v-if forces a real mount/unmount on every open, sidestepping whatever
    internal state the library fails to clear.
  -->
  <v-dialog
    v-if="modelValue"
    :model-value="modelValue"
    max-width="440"
    class="caller-dialog-root"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="caller-dialog" role="dialog" aria-modal="true">
      <header class="caller-dialog__header">
        <div class="caller-dialog__brand">
          <div class="caller-dialog__icon" :class="{ 'caller-dialog__icon--danger': danger }" aria-hidden="true">
            <v-icon :icon="icon" size="22" color="white" />
          </div>
          <div>
            <h2 class="caller-dialog__title">{{ title }}</h2>
            <p v-if="subtitle" class="caller-dialog__subtitle">{{ subtitle }}</p>
          </div>
        </div>
        <button type="button" class="caller-dialog__close" aria-label="Close" :disabled="loading" @click="close">
          <v-icon icon="mdi-close" size="20" />
        </button>
      </header>

      <div class="caller-dialog__body caller-dialog__body--center">
        <p class="caller-dialog__confirm">{{ message }}</p>
        <p v-if="detail" class="caller-dialog__meta">{{ detail }}</p>
      </div>

      <footer class="caller-dialog__footer">
        <v-btn variant="text" class="caller-dialog__cancel" :disabled="loading" @click="close">
          {{ cancelLabel }}
        </v-btn>
        <v-btn
          variant="flat"
          class="caller-dialog__submit"
          :class="{ 'caller-dialog__submit--danger': danger }"
          :loading="loading"
          @click="$emit('confirm')"
        >
          {{ confirmLabel }}
        </v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup>
defineOptions({ name: 'SlotConfirmDialog' })

defineProps({
  modelValue: { type: Boolean, default: false },
  icon: { type: String, default: 'mdi-alert-circle-outline' },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  message: { type: String, required: true },
  detail: { type: String, default: '' },
  confirmLabel: { type: String, default: 'Confirm' },
  cancelLabel: { type: String, default: 'Cancel' },
  danger: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

function close() {
  emit('update:modelValue', false)
}
</script>
